// Coquille « Séance de décision » : en-tête de 56 px avec la marque, le sélecteur de
// organisation, la navigation principale (Décisions, Pilotage, Courriers, Dossiers, Finances,
// Territoire, Conseil) et un menu « Plus » ; actions à droite (Aller à, thème, compte).
// En dessous de 1 024 px, la navigation se replie dans un menu ; sur téléphone, une navigation
// basse à quatre entrées plus Menu remplace l'en-tête de navigation.
import { ChevronDown, Menu, X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { classes } from '../outils/classes';
import { Lien } from '../outils/lien';
import type { Icone } from './Bouton';
import styles from './Coquille.module.css';

export interface EntreeNavigation {
  id: string;
  libelle: string;
  href: string;
  icone: Icone;
  /** Compteur (non-lus, en attente) ; masqué si absent ou nul. */
  badge?: number | null;
  libelleBadge?: string;
}

function Badge({ entree, className }: { entree: EntreeNavigation; className?: string }) {
  if (!entree.badge) return null;
  return (
    <span className={classes(styles.badge, 'cc-mono', className)}>
      {entree.badge}
      <span className="cc-masque"> {entree.libelleBadge ?? 'en attente'}</span>
    </span>
  );
}

function LienEntree({ entree, actif, onChoix, className }: { entree: EntreeNavigation; actif?: string; onChoix?: () => void; className: string }) {
  const IconeEntree = entree.icone;
  return (
    <Lien href={entree.href} className={className} aria-current={entree.id === actif ? 'page' : undefined} onClick={onChoix}>
      <IconeEntree aria-hidden strokeWidth={1.75} />
      <span className={styles.libelleEntree}>{entree.libelle}</span>
      <Badge entree={entree} />
    </Lien>
  );
}

/** Menu « Plus » : entrées secondaires, fermé à la navigation et à Échap. */
function MenuPlus({ entrees, actif, cleNavigation }: { entrees: EntreeNavigation[]; actif?: string; cleNavigation?: string }) {
  const ref = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.open = false;
  }, [cleNavigation]);
  useEffect(() => {
    const echap = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && ref.current) ref.current.open = false;
    };
    window.addEventListener('keydown', echap);
    return () => window.removeEventListener('keydown', echap);
  }, []);
  if (entrees.length === 0) return null;
  const ouvertSurActif = entrees.some((e) => e.id === actif);
  return (
    <details className={styles.plus} ref={ref}>
      <summary className={classes(styles.entree, ouvertSurActif && styles.entreeActive)} aria-haspopup="menu">
        Plus
        <ChevronDown aria-hidden strokeWidth={1.75} width={14} height={14} />
      </summary>
      <ul className={styles.menuPlus} role="menu">
        {entrees.map((e) => (
          <li key={e.id} role="none">
            <LienEntree entree={e} actif={actif} className={classes(styles.entreeMenu)} />
          </li>
        ))}
      </ul>
    </details>
  );
}

export interface ProprietesNavigationBasse {
  entrees: EntreeNavigation[];
  actif?: string;
  menuOuvert: boolean;
  onMenu: () => void;
  idTiroir: string;
}

/** Navigation basse (téléphone) : quatre entrées et un bouton Menu. */
export function NavigationBasse({ entrees, actif, menuOuvert, onMenu, idTiroir }: ProprietesNavigationBasse) {
  return (
    <nav className={styles.navBasse} aria-label="Navigation rapide">
      <ul>
        {entrees.slice(0, 4).map((e) => {
          const IconeEntree = e.icone;
          return (
            <li key={e.id}>
              <Lien href={e.href} className={styles.entreeBasse} aria-current={e.id === actif ? 'page' : undefined}>
                <IconeEntree aria-hidden strokeWidth={1.75} />
                {e.libelle}
                <Badge entree={e} className={styles.badgeBas} />
              </Lien>
            </li>
          );
        })}
        <li>
          <button type="button" className={styles.entreeBasse} aria-expanded={menuOuvert} aria-controls={idTiroir} onClick={onMenu}>
            {menuOuvert ? <X aria-hidden strokeWidth={1.75} /> : <Menu aria-hidden strokeWidth={1.75} />}
            Menu
          </button>
        </li>
      </ul>
    </nav>
  );
}

export interface ProprietesCoquille {
  /** Marque : logo et nom. */
  marque: ReactNode;
  /** Sélecteur ou libellé de l’organisation active, à droite de la marque. */
  contexte?: ReactNode;
  /** Entrées principales de l'en-tête. */
  entrees: EntreeNavigation[];
  /** Entrées du menu « Plus ». */
  entreesSecondaires?: EntreeNavigation[];
  /** Identifiants des quatre entrées de la navigation basse. */
  entreesRapides: string[];
  actif?: string;
  /** Actions de l'en-tête : Aller à, thème, compte. */
  actions?: ReactNode;
  /** Pied : organisation, indicateur d'environnement (affiché dans le menu replié et en bas de page). */
  pied?: ReactNode;
  /** Bandeaux fixes (hors connexion) affichés au-dessus du contenu. */
  bandeaux?: ReactNode;
  children: ReactNode;
  /** Clé de navigation : les menus se referment à chaque changement de page. */
  cleNavigation?: string;
}

/** Coquille adaptée aux trois largeurs : en-tête (ordinateur), menu replié (tablette), navigation basse (téléphone). */
export function Coquille({ marque, contexte, entrees, entreesSecondaires = [], entreesRapides, actif, actions, pied, bandeaux, children, cleNavigation }: ProprietesCoquille) {
  const [menuOuvert, setMenuOuvert] = useState(false);
  const idTiroir = useId();
  useEffect(() => setMenuOuvert(false), [cleNavigation]);
  useEffect(() => {
    if (!menuOuvert) return;
    const echap = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOuvert(false);
    };
    window.addEventListener('keydown', echap);
    return () => window.removeEventListener('keydown', echap);
  }, [menuOuvert]);
  const toutes = [...entrees, ...entreesSecondaires];
  const rapides = entreesRapides.map((id) => toutes.find((e) => e.id === id)).filter((e): e is EntreeNavigation => Boolean(e));

  return (
    <div className={`${styles.coquille} eec-coquille`}>
      <aside className={styles.railRefonte}>
        <div className={styles.marqueRail}>{marque}</div>
        <nav aria-label="Navigation latérale"><ul className={styles.railListe}>{toutes.map(e => <li key={e.id}><LienEntree entree={e} actif={actif} className={styles.entreeRail!} /></li>)}</ul></nav>
      </aside>
      <a className="cc-evitement" href="#contenu-principal">
        Aller au contenu
      </a>
      <header className={styles.entete}>
        <div className={styles.marqueEntete}>
          {marque}
          {contexte ? (
            <>
              <span className={styles.separateur} aria-hidden>
                ·
              </span>
              {contexte}
            </>
          ) : null}
        </div>
        <nav className={styles.nav} aria-label="Navigation principale">
          <ul className={styles.navListe}>
            {entrees.map((e) => (
              <li key={e.id}>
                <LienEntree entree={e} actif={actif} className={classes(styles.entree)} />
              </li>
            ))}
            {entreesSecondaires.length ? (
              <li>
                <MenuPlus entrees={entreesSecondaires} actif={actif} cleNavigation={cleNavigation} />
              </li>
            ) : null}
          </ul>
        </nav>
        <button type="button" className={styles.boutonMenu} aria-expanded={menuOuvert} aria-controls={idTiroir} onClick={() => setMenuOuvert((o) => !o)}>
          {menuOuvert ? <X aria-hidden strokeWidth={1.75} width={20} height={20} /> : <Menu aria-hidden strokeWidth={1.75} width={20} height={20} />}
          Menu
        </button>
        <div className={styles.actionsEntete}>{actions}</div>
      </header>
      <nav id={idTiroir} className={styles.tiroir} data-ouvert={menuOuvert} aria-label="Menu" hidden={!menuOuvert}>
        <ul className={styles.tiroirListe}>
          {toutes.map((e) => (
            <li key={e.id}>
              <LienEntree entree={e} actif={actif} className={classes(styles.entreeMenu)} onChoix={() => setMenuOuvert(false)} />
            </li>
          ))}
        </ul>
        {pied ? <div className={styles.piedTiroir}>{pied}</div> : null}
      </nav>
      <main id="contenu-principal" className={styles.contenu} tabIndex={-1}>
        {bandeaux}
        {children}
      </main>
      {pied ? <footer className={styles.pied}>{pied}</footer> : null}
      <NavigationBasse entrees={rapides} actif={actif} menuOuvert={menuOuvert} onMenu={() => setMenuOuvert((o) => !o)} idTiroir={idTiroir} />
    </div>
  );
}

export interface ProprietesMarque {
  href: string;
  /** Image de la marque (bâtiment), proportions conservées. */
  image: string;
  /** Variante pour le thème nuit, si elle diffère. */
  imageNuit?: string;
  nom?: string;
}

/** Marque de l'en-tête : bâtiment et nom. */
export function Marque({ href, image, imageNuit, nom = 'Eau Énergie Connect' }: ProprietesMarque) {
  return (
    <Lien href={href} className={styles.marque} aria-label={`${nom}, accueil`}>
      <img src={image} alt="" width={26} height={22} className={imageNuit ? 'cc-logo-jour' : undefined} />
      {imageNuit ? <img src={imageNuit} alt="" width={26} height={22} className="cc-logo-nuit" /> : null}
      <span>{nom}</span>
    </Lien>
  );
}

const LIBELLES_ENVIRONNEMENT: Record<string, string | null> = {
  developpement: 'Environnement de développement',
  presentation: 'Environnement de présentation',
  recette: 'Environnement de recette',
  production: null,
};

/** Ligne discrète indiquant l'environnement (rien en production). */
export function IndicateurEnvironnement({ environnement, className }: { environnement: string; className?: string }) {
  const libelle = environnement in LIBELLES_ENVIRONNEMENT ? LIBELLES_ENVIRONNEMENT[environnement] : `Environnement : ${environnement}`;
  if (!libelle) return null;
  return (
    <span className={classes(styles.environnement, className)} data-environnement={environnement}>
      {libelle}
    </span>
  );
}

/** Compatibilité : anciennes exportations du rail et de la barre, désormais intégrées à la coquille. */
export const Rail = (): null => null;
export const BarreSuperieure = (): null => null;
